import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { insertTrackEvent } from '@comeon/analytics';
import { generalError } from '../../ducks/index.js';
import { updateExecuteDepositResponse } from '../pages/execute-deposit/ducks/index.js';
import actualSendHandlerEffect from '../../deposit/data/actual-sendhandler-effect.js';
import { sendThreeDSData } from '../../deposit/components/get-finger-print-data.js';
import usePrevious from '~/hooks/use-previous.js';
import { updatebankIdDepositResult, externalIframeRequest } from '~/components/brand/ducks/index.js';
import useConfiguration from '~/configuration/use-configuration.js';
import useRouterCallbacks from '~/hooks/use-router-callbacks';
import { DepositTypes } from '~/components/payment/types';

const useExecuteDeposit = ({ pending, qdDepositFlow }) => {
	const [encodedJSON, setEncodedJSON] = useState({});
	const [updateFormAttr, setpdUateFormAttr] = useState(false);
	const [threeDSMethodURL, setThreeDSMethodURL] = useState(null);
	const [UrlParams, seturlParams] = useState(false);
	const [redirectToRegistration, setRedirectToRegistration] = useState(false);
	const [failDepositEventSent, setFailDepositEventSent] = useState(false);
	const [twoFactorAuth, settwoFactorAuth] = useState(false);
	const [promptNativeAppInfo, setPromptNativeAppInfo] = useState(false);

	const { transformPathAndPush } = useRouterCallbacks();
	const { isDesktop, isIntegrationTesting } = useConfiguration();

	const payment = useSelector(state => state.payment);
	const result = useSelector(state => state.payment.result);
	const validForSubmit = useSelector(state => state.payment.spec);

	const dispatch = useDispatch();
	const location = useLocation();
	const formRef = useRef();
	const prevProps = usePrevious({ result });

	const doneCallback = (data, retryTimeout) => {
		if (retryTimeout) {
			return dispatch(
				generalError({
					message: 'Retry timeout Error',
					title: 'General Error'
				})
			);
		}
		return dispatch(updateExecuteDepositResponse(data));
	};

	const bindThreeDSListenerEvent = (threeDSMethodurl, postUrl, externalParameters) => {
		const onError = threeDSData => {
			return dispatch(
				generalError({
					message: `Retry timeout Error + ${threeDSData || ''}`,
					title: 'General Error'
				})
			);
		};
		const receiveMessage = event => {
			if (!threeDSMethodurl.includes(event.origin)) return;
			if (event.data) {
				let jsonData = {};
				try {
					// Check if data is already an object
					if (typeof event.data === 'object') {
						jsonData = event.data;
					} else if (typeof event.data === 'string') {
						jsonData = JSON.parse(event.data);
					} else {
						onError(event.data);
					}
				} catch (error) {
					onError();
				}

				const payload = { postUrl, ...jsonData, externalParameters };
				sendThreeDSData(payload, doneCallback, onError);
				window.removeEventListener('message', receiveMessage);
			}
		};
		window.addEventListener('message', receiveMessage);
	};

	const setUrlParams = (accHistRef, urlParams) => {
		const oneButtonFlow = payment?.status?.isBankIdDeposit;
		seturlParams(prevState => {
			return !prevState || prevState.accHistRef !== accHistRef ? urlParams : false;
		});
		if (oneButtonFlow) {
			const payload = {
				paymentType: 'deposit',
				queryParameters: { ...urlParams }
			};
			dispatch(updatebankIdDepositResult(payload));
		}
	};

	const formRedirectOnHiddenIframe = (serverTransactionID, threeDSMethodurl, externalParameters) => {
		setEncodedJSON(externalParameters.threeDSMethodData || externalParameters);
		setThreeDSMethodURL(threeDSMethodurl);
		setpdUateFormAttr(!externalParameters.threeDSMethodData);
	};

	const setTwoFact = () => {
		settwoFactorAuth(prevState => !prevState);
	};

	const updateComponentState = () => {
		const canBuildResult = validForSubmit && !pending && result;

		if (!canBuildResult) return;

		const { error } = result;
		if (error) {
			if (!failDepositEventSent) {
				const { isFirstDeposit } = payment?.lastUsedMethodDetails || {};
				const { methodId } = payment?.status || {};
				const paymentMethods = payment?.paymentMethods || {};
				const paymentMethod = paymentMethods[methodId]?.methodCode;

				let eventAction = '';

				if (isFirstDeposit) {
					eventAction = 'First - Failed';
				} else {
					eventAction = 'Recurring - Failed';
				}

				insertTrackEvent({
					category: 'Deposit',
					action: eventAction,
					label: `${paymentMethod} - ${result?.title} - ${result?.message} - ${DepositTypes.NORMAL_DEPOSIT}`,
					value: validForSubmit?.am
				});
				setFailDepositEventSent(true);
			}
			return;
		}
		actualSendHandlerEffect({
			result,
			setUrlParams,
			isDesktop,
			qdDepositFlow,
			isIntegrationTesting,
			dispatch,
			location,
			setPromptNativeAppInfo,
			doneCallback,
			prevProps,
			formRedirectOnHiddenIframe,
			bindThreeDSListenerEvent,
			setTwoFact,
			transformPathAndPush
		});
	};

	useEffect(() => {
		if (threeDSMethodURL && formRef.current) {
			formRef.current.submit();
		}
	}, [threeDSMethodURL]);

	useEffect(() => {
		updateComponentState();

		// clean up iframe data on unmount
		return () =>
			dispatch(
				externalIframeRequest({
					iframeExternalRequestUrl: '',
					iframeExternalParams: {}
				})
			);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [result]);

	return {
		formRef,
		encodedJSON,
		threeDSMethodURL,
		updateFormAttr,
		UrlParams,
		redirectToRegistration,
		twoFactorAuth,
		promptNativeAppInfo,
		payment,
		setTwoFact,
		setRedirectToRegistration,
		result,
		validForSubmit
	};
};

export default useExecuteDeposit;
